import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Zap, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Admin-only card on the Credits page: when the balance drops to a threshold, the backend
// auto-generates a Stripe payment link and notifies admins to approve it with one click.
const AutoRechargeCard = ({ packages = [] }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_super_admin;

  const [config, setConfig] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [packageId, setPackageId] = useState('');
  const [threshold, setThreshold] = useState(10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/credits/auto-recharge/status`);
      setConfig(res.data);
      setEnabled(res.data.enabled);
      setPackageId(res.data.package_id || packages[0]?.id || '');
      setThreshold(res.data.threshold ?? res.data.default_threshold ?? 10);
    } catch (err) {
      // Non-admins may not have access; fail silently
    }
  };

  const saveConfig = async (nextEnabled, successMsg) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('enabled', nextEnabled);
      if (nextEnabled) {
        formData.append('package_id', packageId);
        formData.append('threshold', threshold || 1);
      }
      await axios.post(`${API}/credits/auto-recharge/config`, formData);
      setEnabled(nextEnabled);
      toast.success(successMsg);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update auto top-up');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (nextEnabled) => {
    if (nextEnabled && !packageId) {
      toast.error('Choose a credit package first');
      return;
    }
    saveConfig(nextEnabled, nextEnabled ? 'Auto top-up enabled' : 'Auto top-up disabled');
  };

  const handleSaveSettings = () => {
    if (!packageId) {
      toast.error('Choose a credit package first');
      return;
    }
    saveConfig(true, 'Settings saved');
  };

  if (!isAdmin || !config) return null;

  return (
    <Card className="p-6 border border-slate-200" data-testid="auto-recharge-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
            <RefreshCw size={18} className="text-violet-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Auto Top-Up</h3>
            <p className="text-sm text-slate-500 mt-0.5 max-w-md">
              When your balance drops to the threshold, we'll generate a payment link and notify
              admins to approve it with one click. No card is ever charged without your consent.
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={handleToggle}
          disabled={saving}
          data-testid="auto-recharge-toggle"
        />
      </div>

      {enabled && (
        <div className="grid sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-slate-100">
          <div>
            <Label className="text-sm text-slate-600">Package to buy</Label>
            <Select value={packageId} onValueChange={setPackageId}>
              <SelectTrigger className="mt-1" data-testid="auto-recharge-package-select">
                <SelectValue placeholder="Choose a package" />
              </SelectTrigger>
              <SelectContent>
                {packages.map((pkg) => (
                  <SelectItem key={pkg.id} value={pkg.id} data-testid={`auto-recharge-package-${pkg.id}`}>
                    {pkg.name} — {pkg.credits} credits (${pkg.price})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-slate-600">Trigger threshold (credits)</Label>
            <Input
              type="number"
              min={1}
              value={threshold}
              onChange={(e) => {
                const val = e.target.value;
                setThreshold(val === '' ? '' : parseInt(val, 10));
              }}
              onBlur={() => {
                if (threshold === '' || isNaN(threshold) || threshold < 1) setThreshold(1);
              }}
              className="mt-1"
              data-testid="auto-recharge-threshold-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Button
              size="sm"
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="auto-recharge-save-button"
            >
              <Zap size={14} className="mr-1.5" />
              {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AutoRechargeCard;
